import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, fileUrl, formatApiErrorDetail, nivelInfo, NIVELES, ESTADO_PAGO } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlumnoDialog } from "@/components/AlumnoDialog";
import { TurnosAlumno } from "@/components/TurnosAlumno";
import { PagosManuales } from "@/components/PagosManuales";
import { HistorialPagos } from "@/components/HistorialPagos";
import { CampamentoAlumno } from "@/components/CampamentoAlumno";
import { NivelAlumnoSelect } from "@/components/NivelAlumnoSelect";
import { ReposicionesAlumno } from "@/components/ReposicionesAlumno";
import { ReconciliarCobertura } from "@/components/ReconciliarCobertura";
import { toast } from "sonner";
import {
  ArrowLeft, Pencil, Trash2, ShieldCheck, ShieldOff, AlertTriangle, Upload, Loader2, ImagePlus,
  RotateCcw, Mail,
} from "lucide-react";

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-2 border-b border-border last:border-0">
    <span className="text-sm text-muted-foreground">{label}</span>
    <span className="text-sm font-semibold text-right">{value || "—"}</span>
  </div>
);

export default function AlumnoDetalle() {
  const { id } = useParams();
  const { user } = useAuth();
  const esProfesor = user?.role === "profesor";
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [alumno, setAlumno] = useState(null);
  const [sedes, setSedes] = useState([]);
  const [clases, setClases] = useState([]);
  const [padres, setPadres] = useState([]);
  const [planes, setPlanes] = useState([]);
  const [resumenPagos, setResumenPagos] = useState(null);
  const [avances, setAvances] = useState([]);
  const [asistencia, setAsistencia] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [avanceOpen, setAvanceOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [enviandoSolicitud, setEnviandoSolicitud] = useState(false);
  const [avance, setAvance] = useState({ titulo: "", comentario: "", file_id: null });

  const load = () => {
    api.get(`/alumnos/${id}`).then(({ data }) => setAlumno(data));
    api.get(`/alumnos/${id}/avances`).then(({ data }) => setAvances(data));
    api.get(`/alumnos/${id}/asistencia`).then(({ data }) => setAsistencia(data))
      .catch(() => setAsistencia({ error: true }));
    api.get(`/alumnos/${id}/resumen-pagos`).then(({ data }) => setResumenPagos(data))
      .catch(() => setResumenPagos({ error: true }));
  };
  useEffect(() => {
    load();
    api.get("/sedes").then(({ data }) => setSedes(data));
    api.get("/clases").then(({ data }) => setClases(data));
    api.get("/padres").then(({ data }) => setPadres(data));
    api.get("/planes").then(({ data }) => setPlanes(data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const sedeName = (sid) => sedes.find((s) => s.id === sid)?.nombre || "—";
  const claseNames = (ids = []) => ids.map((i) => clases.find((c) => c.id === i)?.nombre).filter(Boolean).join(", ");
  const padre = padres.find((p) => p.id === alumno?.padre_id);

  const update = async (payload) => {
    try {
      await api.put(`/alumnos/${id}`, payload);
      toast.success("Ficha actualizada");
      setEditOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const removeAlumno = async () => {
    await api.delete(`/alumnos/${id}`);
    toast.success("Alumno eliminado");
    navigate("/admin/alumnos");
  };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAvance((a) => ({ ...a, file_id: data.file_id }));
      toast.success("Imagen subida");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const saveAvance = async () => {
    try {
      await api.post(`/alumnos/${id}/avances`, avance);
      toast.success("Avance registrado");
      setAvance({ titulo: "", comentario: "", file_id: null });
      setAvanceOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const deleteAvance = async (aid) => {
    await api.delete(`/avances/${aid}`);
    load();
  };

  const cambiarNivel = async (nuevo, motivo = "") => {
    try {
      const { data } = await api.post(`/alumnos/${id}/nivel`, { nivel: nuevo, motivo });
      toast.success(data.pendiente_aprobacion
        ? "Cambio de nivel solicitado: administración lo revisará"
        : "Nivel actualizado");
      load();
      return data;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail), { duration: 9000 });
      throw e;
    }
  };
  void cambiarNivel;

  const reactivar = async () => {    try {
      await api.post(`/alumnos/${id}/reactivar`);
      toast.success("Alumno reactivado: la baja queda revertida");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const solicitarConsentimiento = async () => {
    setEnviandoSolicitud(true);
    try {
      await api.post(`/alumnos/${id}/solicitar-consentimiento`);
      toast.success("Email enviado al tutor con su enlace para completar el perfil");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setEnviandoSolicitud(false);
    }
  };

  if (!alumno) return <p className="text-muted-foreground">Cargando…</p>;

  const nivel = nivelInfo(alumno.nivel);
  const pago = ESTADO_PAGO[alumno.estado_pago] || ESTADO_PAGO.pendiente;

  return (
    <div data-testid="alumno-detalle-page">
      <button onClick={() => navigate("/admin/alumnos")} data-testid="back-to-alumnos"
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors duration-200">
        <ArrowLeft className="h-4 w-4" /> Volver a alumnos
      </button>

      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8 fade-up">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold">{alumno.nombre} {alumno.apellidos}</h1>
            <Badge variant="outline" className="rounded-full gap-1.5" data-testid="detalle-nivel-badge">
              <span className={`h-2 w-2 rounded-full ${nivel.dot}`} /> {nivel.label}
            </Badge>
            <Badge variant={alumno.estado === "activo" ? "default" : "secondary"} className="capitalize">{alumno.estado}</Badge>
          </div>
          <p className="text-muted-foreground mt-2 text-sm">
            Matrícula <b className="text-foreground" data-testid="detalle-matricula">{alumno.matricula}</b> ·{" "}
            {sedes.length ? sedeName(alumno.sede_id) : "…"} ·{" "}
            {clases.length ? (claseNames(alumno.clase_ids) || "Sin clases asignadas") : "…"}
          </p>
        </div>
        <div className="flex gap-2">
          {alumno.padre_id && (
            <Button variant="outline" className="rounded-full" onClick={solicitarConsentimiento}
              disabled={enviandoSolicitud} data-testid="solicitar-consentimiento-btn">
              {enviandoSolicitud ? <Loader2 className="h-4 w-4 animate-spin" />
                : <><Mail className="h-4 w-4 mr-2" /> Solicitar datos y consentimiento</>}
            </Button>
          )}
          {alumno.baja_solicitada && (
            <Button variant="outline" className="rounded-full" onClick={reactivar}
              data-testid="reactivar-alumno-btn">
              <RotateCcw className="h-4 w-4 mr-2" /> Reactivar alumno
            </Button>
          )}
          <Button variant="outline" className="rounded-full" onClick={() => setEditOpen(true)} data-testid="edit-alumno-btn">
            <Pencil className="h-4 w-4 mr-2" /> Editar ficha
          </Button>
          <Button variant="outline" className="rounded-full text-destructive" onClick={removeAlumno} data-testid="delete-alumno-btn">
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="rounded-2xl p-6 lg:col-span-2 border-border shadow-sm" data-testid="card-datos-alumno">
          <h3 className="font-display text-lg font-bold mb-4">Datos del alumno</h3>
          <Row label="Fecha de nacimiento" value={alumno.fecha_nacimiento} />
          <div className="flex items-center justify-between gap-4 py-2.5 border-b border-border last:border-0">
            <span className="text-sm text-muted-foreground">Nivel</span>
            <NivelAlumnoSelect alumno={{ ...alumno, id }} esProfesor={esProfesor} onCambio={load} />
          </div>
          <Row label="Alumno desde" value={alumno.fecha_alta} />
          <Row label="Clases" value={clases.length ? claseNames(alumno.clase_ids) : "…"} />
        </Card>

        <TurnosAlumno alumnoId={id} onCambio={load} />

        <Card className={`rounded-2xl p-6 border shadow-sm ${alumno.seguro_reposicion ? "border-primary/50 bg-accent/40" : "border-border"}`}
          data-testid="card-seguro">
          <div className="flex items-center gap-2 mb-3">
            {alumno.seguro_reposicion ? <ShieldCheck className="h-5 w-5 text-primary" /> : <ShieldOff className="h-5 w-5 text-muted-foreground" />}
            <h3 className="font-display text-lg font-bold">Seguro de recuperación de clases</h3>
          </div>
          <Badge variant={alumno.seguro_reposicion ? "default" : "secondary"} data-testid="seguro-badge">
            {alumno.seguro_reposicion ? "Contratado" : "No contratado"}
          </Badge>
          <p className="text-sm text-muted-foreground mt-4">
            {alumno.seguro_reposicion
              ? "El alumno puede recuperar las clases a las que falte."
              : "Si el alumno falta, la clase se descuenta y no puede reponerse."}
          </p>
          <Row label="Clases disponibles en bono" value={alumno.clases_disponibles ?? 0} />
        </Card>

        <Card className="rounded-2xl p-6 border-border shadow-sm" data-testid="card-tutor">
          <h3 className="font-display text-lg font-bold mb-4">Padre / Madre / Tutor</h3>
          <Row label="Nombre" value={padre ? `${padre.nombre} ${padre.apellidos}` : "—"} />
          <Row label="NIF/CIF" value={padre?.nif} />
          <Row label="Teléfono" value={padre?.telefono} />
          <Row label="Email" value={padre?.email} />
          <Row label="Dirección" value={padre ? `${padre.direccion || ""} ${padre.ciudad || ""} ${padre.codigo_postal || ""}`.trim() : ""} />
        </Card>

        <Card className="rounded-2xl p-6 border-border shadow-sm" data-testid="card-salud">
          <h3 className="font-display text-lg font-bold mb-4">Observaciones y GDPR</h3>
          <Row label="Observaciones" value={alumno.observaciones} />
          <Row label="GDPR aceptado" value={alumno.gdpr_aceptado ? `Sí (${alumno.gdpr_fecha || "s/f"})` : "No"} />
          <Row label="Derechos de imagen" value={alumno.gdpr_imagen ? "Autorizado" : "No autorizado"} />
        </Card>

        <Card className="rounded-2xl p-6 border-border shadow-sm" data-testid="card-asistencia">
          <h3 className="font-display text-lg font-bold mb-4">Asistencia</h3>
          {!asistencia ? (
            <Loader2 className="h-5 w-5 animate-spin text-primary" data-testid="asistencia-cargando" />
          ) : asistencia.error ? (
            <p className="text-sm text-muted-foreground" data-testid="asistencia-error">
              No se ha podido cargar la asistencia.
            </p>
          ) : (
            <>
              <div className="flex items-baseline gap-2 mb-3">
                <span className="font-display text-4xl font-extrabold" data-testid="alumno-pct-asistencia">
                  {asistencia.porcentaje_asistencia ?? 0}%
                </span>
                <span className="text-sm text-muted-foreground">de {asistencia.total ?? 0} clases</span>
              </div>
              <Row label="Asistidas" value={asistencia.totales?.presente ?? 0} />
              <Row label="Faltas" value={asistencia.totales?.ausente ?? 0} />
              <Row label="Justificadas" value={asistencia.totales?.justificada ?? 0} />
              <Row label="Reposiciones usadas" value={asistencia.totales?.reposicion ?? 0} />
              <Row label="Reposiciones pendientes" value={asistencia.reposiciones_pendientes ?? 0} />
              {(asistencia.detalle || []).slice(0, 6).map((d, i) => (
                <div key={i} className="flex justify-between py-1.5 text-sm" data-testid={`alumno-asistencia-fila-${i}`}>
                  <span className="text-muted-foreground">{d.fecha} · {d.clase}</span>
                  <span className="font-semibold capitalize">{d.estado}</span>
                </div>
              ))}
            </>
          )}
        </Card>

        <Card className="rounded-2xl p-6 border-border shadow-sm" data-testid="card-pago">
          <h3 className="font-display text-lg font-bold mb-4">Suscripción y pago</h3>
          <div className="mb-3">
            <span className={`inline-flex px-3 py-1 rounded-full text-sm font-semibold border ${pago.cls}`} data-testid="detalle-estado-pago">
              {pago.label}
            </span>
          </div>
          <Row label="Tipo" value={<span className="capitalize">{alumno.tipo_suscripcion}</span>} />
          <Row label="Forma de pago" value={<span className="capitalize">{alumno.forma_pago}</span>} />
          <Row label="Importe" value={`${alumno.importe} €`} />
          <Row label="Último pago" value={(alumno.ultimo_pago || "").slice(0, 10)} />
          <Row label="Periodo pagado" value={alumno.inscripcion_hasta ? `${alumno.inscripcion_desde} → ${alumno.inscripcion_hasta}` : "—"} />
          <Row label="Plan contratado" value={alumno.plan_nombre} />
          {alumno.clases_disponibles > 0 && (
            <Row label="Clases de bono" value={`${alumno.clases_disponibles}${alumno.bono_hasta ? ` · caduca el ${alumno.bono_hasta}` : ""}`} />
          )}
          <Row label="Matrícula pagada hasta" value={alumno.matricula_pagada_hasta} />
          <Row label="Seguro válido hasta" value={alumno.seguro_hasta} />
          <Row label="Block de dibujo hasta" value={alumno.block_hasta} />
          <div className="mt-5 pt-4 border-t border-border">
            {!resumenPagos ? (
              <Loader2 className="h-5 w-5 animate-spin text-primary" data-testid="pagos-cargando" />
            ) : (
              <>
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-sm text-muted-foreground">Total pagado</span>
                  <span className="font-display text-2xl font-extrabold" data-testid="alumno-total-pagado">
                    {(resumenPagos.neto ?? 0).toFixed(2)} €
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {resumenPagos.cobros ?? 0} cobro(s)
                  {resumenPagos.devuelto ? ` · ${resumenPagos.devuelto.toFixed(2)} € devueltos` : ""}
                  {resumenPagos.pendiente ? ` · ${resumenPagos.pendiente.toFixed(2)} € pendientes` : ""}
                </p>
              </>
            )}
          </div>
          <div className="mt-5">
            <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">
              Planes vigentes
            </p>
            {!resumenPagos ? (
              <Loader2 className="h-4 w-4 animate-spin text-primary" data-testid="vigentes-cargando" />
            ) : (resumenPagos.vigentes || []).length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="alumno-sin-vigentes">
                Ahora mismo no tiene nada vigente.
              </p>
            ) : (
              <div className="space-y-2">
                {resumenPagos.vigentes.map((v, i) => (
                  <div key={i} className="flex items-start justify-between gap-3 text-sm"
                    data-testid={`alumno-vigente-${i}`}>
                    <span>
                      <span className="font-semibold">{v.concepto}</span>
                      {v.detalle && <span className="text-muted-foreground"> · {v.detalle}</span>}
                    </span>
                    {v.hasta && <span className="text-xs text-muted-foreground shrink-0">hasta {v.hasta}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>

        <PagosManuales alumnoId={id} sedeId={alumno.sede_id} onCambio={load} />
        {user?.role === "admin" && <ReconciliarCobertura alumnoId={id} onCambio={load} />}
        <CampamentoAlumno alumno={alumno} onCambio={load} />
        <ReposicionesAlumno alumnoId={id} />
        <HistorialPagos alumnoId={id} />
      </div>

      <div className="flex items-end justify-between mt-12 mb-6">
        <div>
          <h2 className="font-display text-2xl font-extrabold">Galería de avances</h2>
          <p className="text-sm text-muted-foreground mt-1">Visible también para la familia en su área privada</p>
        </div>
        <Button className="rounded-full font-semibold" onClick={() => setAvanceOpen(true)} data-testid="add-avance-btn">
          <ImagePlus className="h-4 w-4 mr-2" /> Añadir avance
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {avances.map((a) => (
          <Card key={a.id} className="rounded-2xl overflow-hidden border-border shadow-sm group" data-testid={`avance-card-${a.id}`}>
            {a.file_id && (
              <div className="relative overflow-hidden h-56">
                <img src={fileUrl(a.file_id)} alt={a.titulo}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
            )}
            <div className="p-5">
              <p className="font-display font-bold">{a.titulo || "Avance"}</p>
              {a.comentario && <p className="text-sm text-muted-foreground mt-2">{a.comentario}</p>}
              <div className="flex items-center justify-between mt-4">
                <span className="text-xs text-muted-foreground">{a.autor} · {(a.created_at || "").slice(0, 10)}</span>
                <button onClick={() => deleteAvance(a.id)} data-testid={`delete-avance-${a.id}`}>
                  <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive transition-colors duration-200" />
                </button>
              </div>
            </div>
          </Card>
        ))}
        {avances.length === 0 && (
          <p className="text-sm text-muted-foreground" data-testid="avances-empty">
            Aún no hay avances registrados para este alumno.
          </p>
        )}
      </div>

      <AlumnoDialog open={editOpen} onOpenChange={setEditOpen} sedes={sedes} clases={clases} padres={padres}
        planes={planes}
        onSave={update} initial={alumno} />

      <Dialog open={avanceOpen} onOpenChange={setAvanceOpen}>
        <DialogContent className="rounded-2xl" data-testid="avance-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Nuevo avance</DialogTitle></DialogHeader>
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label>Título</Label>
              <Input value={avance.titulo} onChange={(e) => setAvance({ ...avance, titulo: e.target.value })}
                placeholder="Bodegón a lápiz" data-testid="avance-titulo-input" />
            </div>
            <div className="grid gap-2">
              <Label>Comentario del profesor</Label>
              <Textarea value={avance.comentario} onChange={(e) => setAvance({ ...avance, comentario: e.target.value })} data-testid="avance-comentario-input" />
            </div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPickFile} data-testid="avance-file-input" />
            <Button variant="outline" className="rounded-lg" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="avance-upload-btn">
              {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
              {avance.file_id ? "Imagen lista ✓" : "Subir imagen"}
            </Button>
          </div>
          <DialogFooter>
            <Button className="rounded-full font-semibold" onClick={saveAvance} data-testid="save-avance-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
